import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/shell/PageHeader';
import { menuIcons } from '../components/shell/menuIcons';
import { Card, CardBody } from '../components/ui/Card';
import { EmptyState } from '../components/ui/States';
import { useTenant } from '../auth/TenantContext';
import { CreateWorkspaceForm } from '../components/workspace/CreateWorkspaceForm';
import { EditWorkspaceForm } from '../components/workspace/EditWorkspaceForm';
import { DeleteWorkspaceCard } from '../components/workspace/DeleteWorkspaceCard';
import { WorkspaceApprovalsSection } from '../components/workspace/WorkspaceApprovalsSection';
import { WorkspaceMediaSection } from '../components/workspace/WorkspaceMediaSection';
import { WorkspaceTemplatesSection } from '../components/workspace/WorkspaceTemplatesSection';
import { TestModeBanner } from '../features/testMode/TestModeBanner';
import { APPROVALS_ANCHOR, scrollToApprovals } from '../features/testMode/testMode';

/**
 * Full-page create/edit for a Brand Workspace, rendered in the main pane
 * (replaces the old modal, which overflowed on tall forms).
 */
export function WorkspaceFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { activeTenant } = useTenant();

  /**
   * Arriving from a test-mode banner's "Edit Brand Workspace" link, which points at #approvals.
   * A browser scrolls to a hash on load; a client-side route change does not, and the section
   * isn't in the DOM until `activeTenant` resolves — hence the dependency on it, and the frame's
   * delay so the sections below have laid out before we measure where to stop.
   */
  useEffect(() => {
    if (hash !== `#${APPROVALS_ANCHOR}`) return;
    const frame = requestAnimationFrame(scrollToApprovals);
    return () => cancelAnimationFrame(frame);
  }, [hash, activeTenant?.id]);

  if (mode === 'edit' && !activeTenant) {
    return (
      <>
        <PageHeader title="Edit Brand Workspace" icon={menuIcons.workspaceEdit} />
        <Card>
          <CardBody>
            <EmptyState
              title="No active workspace"
              description="Select or create a Brand Workspace first."
            />
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <div className="w-full space-y-4">
      {mode === 'create' ? (
        <>
          <PageHeader
            title="Create Brand Workspace"
            icon={menuIcons.workspaceCreate}
            description="A workspace keeps a brand's content, sources, and digests fully separate. You can switch between workspaces any time."
          />
          <Card>
            <CardBody>
              <CreateWorkspaceForm
                submitLabel="Create Brand Workspace"
                onCreated={() => navigate('/')}
                onCancel={() => navigate(-1)}
              />
            </CardBody>
          </Card>
        </>
      ) : (
        <>
          <PageHeader
            title="Edit Brand Workspace"
            icon={menuIcons.workspaceEdit}
            description="Update this workspace's name, description, Brand Voice, and brand assets."
          />

          <TestModeBanner />
          {/* Each key REMOUNTS its component when the active workspace changes, so form state
              re-initializes from the newly-selected workspace (otherwise the form keeps the old
              values — and a save would write them to the new one). The keys MUST be unique among
              siblings: these sections are all siblings, so a shared `activeTenant.id` key breaks
              React reconciliation and leaves the previous card mounted on switch — hence the
              distinct prefixes. EditWorkspaceForm renders its OWN section cards (Brand Workspace
              Details + Brand Assets & Style), each with its own Save button. */}
          <EditWorkspaceForm
            key={`edit-${activeTenant!.id}`}
            workspace={activeTenant!}
            onCancel={() => navigate(-1)}
          />

          {/* Approvals section — Workspace approval + Reviewer flow + Approval Role Assignment
              together, each with their own Save button. Keyed to remount (re-init) on switch. */}
          {/* The anchor the test-mode banners link to, from this page and every other. `scroll-mt`
              keeps the card's heading clear of the top edge when it lands. */}
          <div id={APPROVALS_ANCHOR} className="scroll-mt-24">
            <WorkspaceApprovalsSection
              key={`approvals-${activeTenant!.id}`}
              workspace={activeTenant!}
            />
          </div>

          {/* Image-generation limit — its own Save button, same remount-on-switch rule. */}
          <WorkspaceMediaSection key={`media-${activeTenant!.id}`} workspace={activeTenant!} />

          {/* Design templates — the Figma/Canva layouts Generate Design fills. Above the Danger
              Zone and below the generation limit, since it belongs with the other media settings.
              Keyed to remount on switch like its siblings. */}
          <WorkspaceTemplatesSection key={`templates-${activeTenant!.id}`} workspace={activeTenant!} />

          {/* Danger Zone at the very bottom — delete this Brand Workspace (Workspace-Admin-only). */}
          <DeleteWorkspaceCard key={`danger-${activeTenant!.id}`} workspace={activeTenant!} />
        </>
      )}
    </div>
  );
}
